import * as log from 'loglevel';
import {
  refreshAccessToken as refreshAxiosAccessToken,
  getCachedAccessToken,
  isInteractionRequiredError,
} from './axiosInstance';
import { performAutoLogout } from '../utils/sessionTimeout';

const timeout = 600000;
const defaultHeaders = {
  'Accept-Language': 'en',
};
let token = null;

let isValidToken = () => true;
let login = async () => '';

// Local in-flight guard for the rare case the axios helper isn't available.
// When it is, we delegate to it so axios + fetch share the same underlying
// refresh and never trigger two parallel MSAL calls.
let refreshPromise = null;

const refreshAuthToken = () => {
  if (refreshAxiosAccessToken) {
    return refreshAxiosAccessToken().then(newToken => {
      if (newToken) {
        token = newToken;
      }
      return newToken;
    });
  }
  if (!login) {
    return Promise.reject(new Error('No token refresh handler configured'));
  }
  if (!refreshPromise) {
    refreshPromise = Promise.resolve()
      .then(() => login())
      .then(newToken => {
        if (newToken) {
          token = newToken;
        }
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const updateFetchInstanceLanguage = languageCode => {
  defaultHeaders['Accept-Language'] = languageCode;
};

export const updateFetchTokenHandler = (_token, _login, _isValidToken) => {
  token = _token;
  login = _login;
  isValidToken = _isValidToken;
};

const buildHeaders = (authToken, optionHeaders) => {
  const headers = {
    ...defaultHeaders,
    ...optionHeaders,
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
};

const performFetch = async (url, options, headers, timeoutMs) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(id);
  }
};

export const fetchWithTimeout = async (
  url,
  options = {},
  timeoutMs = timeout,
) => {
  let authToken = token || getCachedAccessToken();
  const tokenValid = isValidToken();

  // Pre-flight: if we already know the token is expired, try to refresh
  // up-front so the request goes out authenticated. A failure here is
  // non-fatal — the request still goes out and the response-side 401 handler
  // will get another chance to refresh + retry.
  if (authToken && !tokenValid) {
    try {
      const newToken = await refreshAuthToken();
      if (newToken) authToken = newToken;
    } catch (e) {
      log.error('Token renewal failed:', e);
    }
  }

  let response = await performFetch(
    url,
    options,
    buildHeaders(authToken, options.headers),
    timeoutMs,
  );

  // Response-side recovery: on a 401, attempt a single refresh + retry.
  // The function is linear (no loop), so we naturally only refresh once
  // per call. If the refresh succeeds and the retry returns 2xx, the
  // caller never sees the 401. If the refresh throws or resolves with no
  // token, drive the user through a fresh sign-in via performAutoLogout
  // (idempotent — guarded by a `loggingOut` flag).
  if (response.status === 401) {
    let refreshError = null;
    let newToken = null;
    try {
      newToken = await refreshAuthToken();
    } catch (e) {
      refreshError = e;
      log.error('Token refresh after 401 failed:', e);
    }

    if (newToken) {
      response = await performFetch(
        url,
        options,
        buildHeaders(newToken, options.headers),
        timeoutMs,
      );
    } else {
      if (!refreshError) {
        log.warn('Token refresh returned no token');
      }
      const reason = isInteractionRequiredError(refreshError)
        ? 'interaction_required'
        : 'expired';
      performAutoLogout(reason);
    }
  }

  if (!response.ok) {
    if (response.status === 406) {
      throw new Error('UNSUPPORTED_LANGUAGE');
    }
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return response;
};
